import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Papa from "papaparse";
import { DateTime } from "luxon";

import Card from "../../components/shared/Card/Card";
import ClickToCopy from "../../components/shared/ClickToCopy";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../../components/shared/StatusPill";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useStockCheckReports from "../../data/useStockCheckReports";
import { PartStatus } from "../../models/Part";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import {
  STOCK_CHECK_REPORT_CSV_HEADERS,
  stockCheckPartStatusLabel,
  stockCheckReportCsvRow,
} from "../../utils/stockCheckReport";

function statusColour(status: PartStatus): StatusPillColour {
  switch (status) {
    case PartStatus.Dismantled:
      return StatusPillColour.Green;
    case PartStatus.QC:
      return StatusPillColour.Blue;
    case PartStatus.Missing:
    case PartStatus.Damaged:
    case PartStatus["Not Accessible"]:
      return StatusPillColour.Red;
    case PartStatus.Sold:
      return StatusPillColour.Purple;
    default:
      return StatusPillColour.Neutral;
  }
}

export default function ViewStockCheckReport() {
  const { t } = useTranslation("reports");
  const { setInfo } = usePageNameContext();
  const { stockCheckId } = useParams();
  const { report } = useStockCheckReports(1, 25, stockCheckId);
  const data = report.data?.data;

  useEffect(() => {
    setInfo({
      name: data ? t("Stock Check — {{location}}", { location: data.warehouseLocationName }) : t("Stock Check Report"),
      desc: data
        ? data.itemCount === 1
          ? t("{{count}} part checked", { count: data.itemCount })
          : t("{{count}} parts checked", { count: data.itemCount })
        : t("Stock check saved from the yard app"),
    });
  }, [setInfo, data, t]);

  if (report.isLoading) {
    return <LoadingWheel />;
  }

  if (!data) {
    return <p className="mt-4 text-sm text-gray-500">{t("That stock check report could not be found.")}</p>;
  }

  const handleDownload = () => {
    const csv = Papa.unparse({
      fields: STOCK_CHECK_REPORT_CSV_HEADERS,
      data: data.items.map(stockCheckReportCsvRow),
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const completed = formatDateTime(data.completed, DateTime.DATE_SHORT).replace(/\//g, "-");
    link.href = url;
    link.download = `stock-check-${data.warehouseLocationName}-${completed}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 grid grid-cols-1 gap-4">
      <Card
        title={data.warehouseLocationName}
        subtitle={
          data.completedByEmail
            ? t("Completed {{date}} by {{email}}", {
                date: formatDateTime(data.completed, DateTime.DATETIME_SHORT),
                email: data.completedByEmail,
              })
            : t("Completed {{date}}", { date: formatDateTime(data.completed, DateTime.DATETIME_SHORT) })
        }
        headerRight={
          <ButtonInput label={t("Download CSV")} classes="" onClick={handleDownload} />
        }
      >
        <div className="overflow-auto">
          <table className="min-w-full divide-y border divide-gray-300">
            <thead>
              <tr className="bg-primary-50 text-gray-800">
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Tag number")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Part category")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Has images")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("On eBay")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Part status")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Part age")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Last stock check")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("Part value")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("MPN")}</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">{t("OEMPN")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 border text-sm">
                    <Link
                      to={`/stock/${item.partDataId}`}
                      className="text-primary-700 hover:text-primary-800 font-medium"
                    >
                      {item.tagNumber || item.partDataId}
                    </Link>
                  </td>
                  <td className="px-3 py-3 border text-sm">{item.categoryName || "—"}</td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={item.hasImages ? t("Yes") : t("No")}
                      colour={item.hasImages ? StatusPillColour.Green : StatusPillColour.Neutral}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={item.isOnEbay ? t("Yes") : t("No")}
                      colour={item.isOnEbay ? StatusPillColour.Green : StatusPillColour.Neutral}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={t(stockCheckPartStatusLabel(item.partStatus), { context: "partStatus" })}
                      colour={statusColour(item.partStatus)}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.partAgeDays === 1 ? t("1 day") : t("{{count}} days", { count: item.partAgeDays })}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {formatDateTime(item.previousStockChecked, DateTime.DATE_SHORT, "—")}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.partValue == null ? "—" : PriceFormat(item.partValue)}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.mpn ? <ClickToCopy text={item.mpn} /> : "—"}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.oemPn ? <ClickToCopy text={item.oemPn} /> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
